import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Envelope,
  Phone,
  CheckCircle,
  XCircle,
  PencilSimple,
  FloppyDisk,
  Clock,
  Buildings,
  ShieldCheck,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import {
  useSupplier,
  useUpdateSupplier,
  useSupplierStats,
  type UpdateSupplierDto,
} from '../../hooks/useSuppliers';

export default function SupplierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UpdateSupplierDto>({});

  const { data: storeSupplier, isLoading, error } = useSupplier(id || null);
  const { data: stats } = useSupplierStats(id || null);
  const updateSupplier = useUpdateSupplier();

  const handleEdit = () => {
    if (storeSupplier) {
      setFormData({
        name: storeSupplier.supplier?.name || '',
        phoneNumber: storeSupplier.supplier?.phoneNumber || '',
        email: storeSupplier.supplier?.email || '',
        company: storeSupplier.supplier?.company || '',
        address: storeSupplier.supplier?.address || '',
        city: storeSupplier.supplier?.city || '',
        country: storeSupplier.supplier?.country || '',
        postalCode: storeSupplier.supplier?.postalCode || '',
        status: storeSupplier.status,
        isActive: storeSupplier.isActive,
        canViewOrders: storeSupplier.canViewOrders,
        canFulfillOrders: storeSupplier.canFulfillOrders,
        canViewInventory: storeSupplier.canViewInventory,
        canViewProducts: storeSupplier.canViewProducts,
        notes: storeSupplier.notes || '',
      });
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({});
  };

  const handleSave = async () => {
    if (!id) return;

    try {
      await updateSupplier.mutateAsync({ id, data: formData });
      setIsEditing(false);
      setFormData({});
    } catch (err) {
      console.error('Failed to update supplier:', err);
      alert('Failed to save changes.');
    }
  };

  if (isLoading) {
    return (
      <div className="p-12 space-y-4 animate-pulse">
        <div className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl" />
        <div className="h-64 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl" />
      </div>
    );
  }

  if (error || !storeSupplier) {
    return (
      <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl p-6 text-center">
        <XCircle weight="fill" className="w-10 h-10 text-red-600 mx-auto mb-2" />
        <h2 className="text-sm font-bold text-red-900 dark:text-red-200">
          Supplier Partner Unavailable
        </h2>
        <p className="text-xs text-red-700 dark:text-red-300 mt-1">
          The requested supplier record could not be loaded.
        </p>
        <button
          type="button"
          onClick={() => navigate('/suppliers')}
          className="mt-4 h-8 px-4 bg-[#006838] text-white text-xs font-semibold rounded-lg"
        >
          Return to Suppliers
        </button>
      </div>
    );
  }

  const { supplier } = storeSupplier;

  const getStatusBadge = (status?: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'APPROVED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
          <CheckCircle weight="fill" className="w-3 h-3" />
          Approved Partner
        </span>
      );
    }
    if (s === 'PENDING') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
          <Clock weight="fill" className="w-3 h-3" />
          Pending Approval
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-700 border border-red-200">
        <XCircle weight="fill" className="w-3 h-3" />
        {s || 'Inactive'}
      </span>
    );
  };

  return (
    <div className="space-y-5">
      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/suppliers')}
            className="p-1.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
            title="Back to supplier directory"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold text-[#202223] dark:text-white">
                {supplier?.name || 'Supplier Details'}
              </h1>
              {getStatusBadge(storeSupplier.status)}
            </div>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              Added on{' '}
              {storeSupplier.createdAt
                ? format(new Date(storeSupplier.createdAt), 'MMMM d, yyyy')
                : 'Unknown date'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={handleCancel}
                className="h-8 px-3 rounded-lg border border-[#D2D5D9] text-xs font-semibold text-[#5C5F62]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={updateSupplier.isPending}
                className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold inline-flex items-center gap-1.5"
              >
                <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
                {updateSupplier.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleEdit}
              className="h-8 px-3.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1.5"
            >
              <PencilSimple weight="bold" className="w-3.5 h-3.5" />
              Edit Supplier
            </button>
          )}
        </div>
      </div>

      {/* KPI Stats Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
            Total Orders Linked
          </span>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
            {(stats as any)?.supplier?.totalOrders ?? (stats as any)?.totalProducts ?? 0}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
            Total Fulfillments
          </span>
          <p className="text-xl font-bold text-[#006838] font-mono mt-1">
            {(stats as any)?.stats?.totalFulfillments ?? (stats as any)?.activeOrders ?? 0}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
            Fulfillment Access
          </span>
          <p className="text-sm font-bold text-[#202223] dark:text-white mt-2">
            {storeSupplier.canFulfillOrders ? 'Enabled' : 'Restricted'}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
            Inventory Access
          </span>
          <p className="text-sm font-bold text-[#202223] dark:text-white mt-2">
            {storeSupplier.canViewInventory ? 'Enabled' : 'Restricted'}
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Contact Details Card */}
        <div className="lg:col-span-2 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
            <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
              Organization & Contact Details
            </h2>
            <Buildings weight="fill" className="w-4 h-4 text-[#5C5F62]" />
          </div>

          {isEditing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] bg-white text-xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] bg-white text-xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={formData.phoneNumber || ''}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] bg-white text-xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] bg-white text-xs"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-xs">
              <div className="flex items-center gap-3">
                <Envelope weight="bold" className="w-4 h-4 text-[#8C9196]" />
                <div>
                  <span className="block text-[#5C5F62]">Email Address</span>
                  <span className="font-semibold text-[#202223] dark:text-white">
                    {supplier?.email || 'No email specified'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone weight="bold" className="w-4 h-4 text-[#8C9196]" />
                <div>
                  <span className="block text-[#5C5F62]">Phone Line</span>
                  <span className="font-semibold font-mono text-[#202223] dark:text-white">
                    {supplier?.phoneNumber || 'Not provided'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Buildings weight="bold" className="w-4 h-4 text-[#8C9196]" />
                <div>
                  <span className="block text-[#5C5F62]">Address & Location</span>
                  <span className="font-semibold text-[#202223] dark:text-white">
                    {supplier?.address ? `${supplier.address}, ` : ''}
                    {supplier?.city || 'Nairobi'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Access Control Permissions Card */}
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
            <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
              Permissions & Access
            </h2>
            <ShieldCheck weight="fill" className="w-4 h-4 text-[#006838]" />
          </div>

          <div className="space-y-3 text-xs">
            <label className="flex items-center justify-between p-2.5 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
              <span className="font-medium text-[#202223] dark:text-white">View Store Orders</span>
              <input
                type="checkbox"
                disabled={!isEditing}
                checked={
                  isEditing ? !!formData.canViewOrders : !!storeSupplier.canViewOrders
                }
                onChange={(e) =>
                  setFormData({ ...formData, canViewOrders: e.target.checked })
                }
                className="rounded border-[#D2D5D9] text-[#006838] focus:ring-[#006838]"
              />
            </label>

            <label className="flex items-center justify-between p-2.5 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
              <span className="font-medium text-[#202223] dark:text-white">Fulfill Orders</span>
              <input
                type="checkbox"
                disabled={!isEditing}
                checked={
                  isEditing
                    ? !!formData.canFulfillOrders
                    : !!storeSupplier.canFulfillOrders
                }
                onChange={(e) =>
                  setFormData({ ...formData, canFulfillOrders: e.target.checked })
                }
                className="rounded border-[#D2D5D9] text-[#006838] focus:ring-[#006838]"
              />
            </label>

            <label className="flex items-center justify-between p-2.5 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
              <span className="font-medium text-[#202223] dark:text-white">
                View Real-time Inventory
              </span>
              <input
                type="checkbox"
                disabled={!isEditing}
                checked={
                  isEditing
                    ? !!formData.canViewInventory
                    : !!storeSupplier.canViewInventory
                }
                onChange={(e) =>
                  setFormData({ ...formData, canViewInventory: e.target.checked })
                }
                className="rounded border-[#D2D5D9] text-[#006838] focus:ring-[#006838]"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
